import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Film, Youtube, Eye, EyeOff } from "lucide-react";
import { endpoints } from "@/lib/api";
import { PageHeader, Card, Field, Btn, DeleteBtn, Loading, Empty, useCrud, handleErr } from "./AdminUI";
import { ImageUpload } from "@/components/ImageUpload";

const SOURCE_TYPES = [
  { v: "youtube", label: "YouTube (URL or video ID)" },
  { v: "mp4", label: "MP4 / direct video URL" },
  { v: "hls", label: "HLS stream (.m3u8)" },
  { v: "iframe", label: "Embed / iframe URL" },
];
const BLANK = { title: "", sourceType: "youtube", sourceValue: "", category: "Shows", thumbnail: "", duration: "", description: "", ctaText: "", premium: false, published: true, showOnHomepage: false, order: 0, programId: "" };

const Select = ({ label, value, onChange, children, ...p }) => (
  <label className="block"><span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{label}</span>
    <select value={value} onChange={onChange} {...p} className="mt-1.5 w-full h-11 rounded-xl bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm">{children}</select>
  </label>
);

export default function AdminShows() {
  const { data: shows, loading, reload } = useCrud(endpoints.adminShowsAll);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [showForm, setShowForm] = useState(false);
  const [importing, setImporting] = useState(false);

  const loadCats = () => endpoints.adminCategories().then((r) => setCategories(r.data)).catch(() => {});
  useEffect(() => { endpoints.adminPrograms().then((r) => setPrograms(r.data)).catch(() => {}); loadCats(); }, []);

  const addCategory = async () => {
    const name = window.prompt("New category name (e.g. Documentaries):");
    if (!name || !name.trim()) return;
    try { const { data } = await endpoints.adminCreateCategory({ name: name.trim(), isActive: true }); toast.success(`Category "${data.name}" created`); await loadCats(); setForm((f) => ({ ...f, category: data.name })); }
    catch (e) { handleErr(e); }
  };

  const create = async () => {
    if (!form.title || !form.sourceValue) { toast.error("Title and source are required"); return; }
    const body = {
      title: form.title, sourceType: form.sourceType, sourceValue: form.sourceValue,
      videoUrl: form.sourceValue, category: form.category, thumbnail: form.thumbnail,
      duration: form.duration, description: form.description, premium: form.premium,
      ctaText: form.ctaText, showOnHomepage: form.showOnHomepage, order: Number(form.order) || 0,
      published: form.published, programId: form.programId || null, origin: "upload",
    };
    try { await endpoints.adminCreateShow(body); toast.success("Show added"); setForm(BLANK); setShowForm(false); reload(); }
    catch (e) { handleErr(e); }
  };
  const del = async (id) => { try { await endpoints.adminDeleteShow(id); toast.success("Deleted"); reload(); } catch (e) { handleErr(e); } };
  const togglePublish = async (s) => {
    try { await endpoints.adminUpdateShow(s.id, { published: !(s.published !== false) }); toast.success(s.published !== false ? "Unpublished" : "Published"); reload(); }
    catch (e) { handleErr(e); }
  };
  const importYt = async () => {
    setImporting(true);
    try { const { data } = await endpoints.adminYoutubeImport({ maxPerChannel: 10 }); toast.success(`Imported ${data.upserted} new video(s) (draft). Publish the ones you want.`); reload(); }
    catch (e) { handleErr(e); } finally { setImporting(false); }
  };

  return (
    <div>
      <PageHeader title="Shows / VOD" desc={`${shows.length} item(s) · YouTube · uploads · live recordings · cloud`}>
        <Btn variant="ghost" onClick={importYt} disabled={importing} data-testid="admin-import-youtube-btn"><Youtube className="w-4 h-4" /> {importing ? "Importing…" : "Import from YouTube"}</Btn>
        <Btn onClick={() => setShowForm((s) => !s)} data-testid="admin-add-show-btn"><Film className="w-4 h-4" /> Add show</Btn>
      </PageHeader>

      {showForm && (
        <Card className="p-5 mb-6 grid sm:grid-cols-2 gap-3">
          <Field label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="show-title-input" />
          <Select label="Source type" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} data-testid="show-sourcetype-select">
            {SOURCE_TYPES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </Select>
          <Field label="Source (URL or YouTube ID)" value={form.sourceValue} onChange={(e) => setForm({ ...form, sourceValue: e.target.value })} data-testid="show-video-input" placeholder="https://… or jfKfPfyJRdk" />
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="show-category-select">
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </Select>
            </div>
            <Btn variant="ghost" onClick={addCategory} data-testid="show-add-category-btn" className="mb-0.5">+ New</Btn>
          </div>
          <ImageUpload label="Thumbnail" value={form.thumbnail} onChange={(url) => setForm({ ...form, thumbnail: url })} testid="show-thumb" />
          <Field label="Duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 45:00" />
          <Select label="Program (optional)" value={form.programId} onChange={(e) => setForm({ ...form, programId: e.target.value })}>
            <option value="">— None —</option>
            {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Field label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Field label="Button / CTA text (customer-facing)" value={form.ctaText} onChange={(e) => setForm({ ...form, ctaText: e.target.value })} data-testid="show-cta-input" placeholder="e.g. Watch now" />
          <Field label="Display order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="show-order-input" />
          <label className="flex items-center gap-2 text-sm text-slate-200 mt-1">
            <input type="checkbox" checked={form.showOnHomepage} onChange={(e) => setForm({ ...form, showOnHomepage: e.target.checked })} data-testid="show-homepage-toggle" className="w-4 h-4 accent-[#E11D2A]" /> Feature on homepage
          </label>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.premium} onChange={(e) => setForm({ ...form, premium: e.target.checked })} data-testid="show-premium-toggle" className="w-4 h-4 accent-[#E11D2A]" />
            <Crown className="w-4 h-4 text-[#2563EB]" /> Premium (members only)
          </label>
          <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="show-published-toggle" className="w-4 h-4 accent-[#E11D2A]" />
            Published (visible to viewers)
          </label>
          <div className="flex items-end"><Btn onClick={create} data-testid="show-save-btn" className="w-full">Save show</Btn></div>
        </Card>
      )}

      {loading ? <Loading /> : shows.length === 0 ? <Empty /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shows.map((s) => (
            <Card key={s.id} data-testid={`admin-show-${s.id}`} className="overflow-hidden">
              <div className="aspect-video relative"><img src={s.thumbnail || "/bb-logo.png"} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = "/bb-logo.png"; }} />
                {s.isPremium && <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-[#2563EB] text-white text-[10px] font-extrabold">PREMIUM</span>}
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded bg-black/70 text-slate-200 text-[10px] font-bold uppercase">{s.sourceType || "youtube"}{s.origin === "youtube" ? " · yt" : s.origin === "live" ? " · live" : ""}</span>
                {s.published === false && <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-amber-600 text-white text-[10px] font-extrabold">DRAFT</span>}
              </div>
              <div className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0"><div className="text-white text-sm font-semibold truncate">{s.title}</div><div className="text-xs text-slate-400 flex items-center gap-2">{s.category}<span className="flex items-center gap-1" data-testid={`admin-show-views-${s.id}`}><Eye className="w-3 h-3" /> {s.views || 0}</span></div></div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => togglePublish(s)} data-testid={`show-publish-${s.id}`} title={s.published !== false ? "Unpublish" : "Publish"}
                    className="w-8 h-8 rounded-lg bg-white/5 border border-[#273042] text-slate-300 hover:text-white flex items-center justify-center">
                    {s.published !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <DeleteBtn onClick={() => del(s.id)} testid={`show-delete-${s.id}`} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
